import { createContext, useContext, type ReactNode } from "react";

import type { CmsApiClient } from "../lib/api";

/**
 * The API client, provided once at the top of the tree.
 *
 * Passed through context rather than imported as a module singleton, because
 * the client carries the `onUnauthorized` behaviour — and a test that mounts a
 * page should be able to supply its own without reaching into module state.
 */
const ApiContext = createContext<CmsApiClient | null>(null);

export interface ApiProviderProps {
  readonly client: CmsApiClient;
  readonly children: ReactNode;
}

export function ApiProvider({ client, children }: ApiProviderProps) {
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

/**
 * The API client.
 *
 * Throws when used outside a provider: that is a wiring bug, and failing at the
 * first render beats every page quietly making unauthenticated requests.
 */
export function useApi(): CmsApiClient {
  const client = useContext(ApiContext);

  if (client === null) {
    throw new Error("useApi must be used inside an ApiProvider");
  }

  return client;
}
